import { useWallet } from '@txnlab/use-wallet-react'
import React, { useState, useEffect } from 'react'
import Faucet from './components/Faucet'
import algosdk from 'algosdk'
import { getAlgodConfigFromViteEnvironment } from './utils/network/getAlgoClientConfigs'
import Donation from './components/Donation'
import ConnectWallet from './components/ConnectWallet'
import CreatorTools from './components/CreatorTools'
import AlgoMark from './components/AlgoMark'
import { FAUCET_APP_ID } from './constants/faucet'

const BLOCK_INTERVAL_DEFAULT = 10_000

const Home: React.FC = () => {
  const { activeAddress } = useWallet()
  const [remainingBlocksText, setRemainingBlocksText] = useState('')
  const [refreshTrigger, setRefreshTrigger] = useState(0)

  useEffect(() => {
    const onRefresh = () => setRefreshTrigger((t) => t + 1)
    window.addEventListener('algofaucet:refresh-balance', onRefresh)
    return () => window.removeEventListener('algofaucet:refresh-balance', onRefresh)
  }, [])

  useEffect(() => {
    const fetchBlockInfo = async () => {
      if (!activeAddress) {
        setRemainingBlocksText('connect your wallet')
        return
      }

      try {
        const algodConfig = getAlgodConfigFromViteEnvironment()
        const algodClient = new algosdk.Algodv2(
          String(algodConfig.token),
          algodConfig.server,
          algodConfig.port
        )

        const status = await algodClient.status().do()
        const currentRound = Number(status.lastRound)

        let blockInterval = BLOCK_INTERVAL_DEFAULT
        try {
          const app = await algodClient.getApplicationByID(FAUCET_APP_ID).do()
          const params = (app as any).params || {}
          const globalState = params['global-state'] ?? params.globalState ?? []
          for (const g of globalState) {
            let key = ''
            try {
              const raw = g.key
              if (typeof raw === 'string') {
                key = Buffer.from(raw, 'base64').toString('utf8').replace(/\0/g, '')
              } else if (raw && typeof raw === 'object' && raw.constructor?.name === 'Uint8Array') {
                key = new TextDecoder().decode(raw).replace(/\0/g, '')
              }
            } catch {
              // skip
            }
            const val = g.value
            const type = val?.type ?? val?.value?.type
            const uintRaw = val?.uint ?? val?.value?.uint
            if (type === 2 && uintRaw !== undefined && uintRaw !== null && key === 'block_interval') {
              const num = Number(uintRaw)
              if (!Number.isNaN(num)) blockInterval = num
              break
            }
          }
        } catch {
          // keep default
        }

        try {
          const senderBytes = algosdk.decodeAddress(activeAddress).publicKey
          const response = await algodClient.getApplicationBoxes(FAUCET_APP_ID).do()
          const hasBox = response.boxes.some((box: any) => {
            const boxName = Buffer.from(box.name, 'base64')
            return Buffer.compare(boxName, senderBytes) === 0
          })

          if (hasBox) {
            const boxResponse = await algodClient.getApplicationBoxByName(FAUCET_APP_ID, senderBytes).do()
            if (boxResponse && boxResponse.value) {
              const valueBuffer = Buffer.from(new Uint8Array(boxResponse.value))
              const isLocked = valueBuffer.length >= 9 && valueBuffer[0] === 1
              const lastClaimValue = valueBuffer.length >= 9
                ? Number(valueBuffer.readBigUInt64BE(1))
                : Number(valueBuffer.readBigUInt64BE(0))

              if (isLocked) {
                setRemainingBlocksText('locked!')
                return
              }

              const remainingBlocks = lastClaimValue + blockInterval - currentRound
              if (remainingBlocks <= 0) {
                setRemainingBlocksText('Claim available!')
              } else {
                setRemainingBlocksText(`Next claim in ${remainingBlocks} blocks`)
              }
            } else {
              setRemainingBlocksText('Claim available!')
            }
          } else {
            setRemainingBlocksText('Claim available!')
          }
        } catch (error: any) {
          if (error.status !== 404) {
            console.error('Error fetching block info:', error)
          }
          setRemainingBlocksText('Claim available!')
        }
      } catch (error) {
        console.error('Error fetching block info:', error)
      }
    }

    fetchBlockInfo()
    if (!activeAddress) return
    const interval = setInterval(fetchBlockInfo, 4000)
    return () => clearInterval(interval)
  }, [activeAddress, refreshTrigger])

  return (
    <div className="site">
      <header className="site-bar">
        <div className="brand">
          <h1 aria-label="AlgoFaucet"><AlgoMark />lgoFaucet</h1>
          <p aria-label="Free ALGO on Mainnet">
            <span>Free&nbsp;</span>
            <span className="brand-word"><AlgoMark />LGO</span>
            <span>&nbsp;on Mainnet</span>
          </p>
        </div>
        <div className="site-actions">
          <CreatorTools />
          <ConnectWallet />
        </div>
      </header>

      <div className="site-body">
        <main className="site-main">
          <Faucet remainingBlocksText={remainingBlocksText} />
        </main>

        <footer className="site-foot">
          <Donation />
        </footer>
      </div>

      <div className="site-socials">
        <a href="https://x.com/AlgoFaucet" target="_blank" rel="noopener noreferrer" aria-label="AlgoFaucet on X">
          <svg viewBox="0 0 24 24" aria-hidden="true">
            <path fill="currentColor" d="M18.244 2H21.5l-7.5 8.57L22.5 22h-6.59l-5.16-6.74L5.2 22H1.94l8.02-9.16L1.5 2h6.76l4.66 6.18L18.244 2zm-1.16 18.1h1.81L6.99 3.8H5.05L17.084 20.1z"/>
          </svg>
        </a>
        <a href="https://www.instagram.com/algofaucet/" target="_blank" rel="noopener noreferrer" aria-label="AlgoFaucet on Instagram">
          <svg viewBox="0 0 24 24" aria-hidden="true">
            <path fill="currentColor" d="M7 3h10a4 4 0 0 1 4 4v10a4 4 0 0 1-4 4H7a4 4 0 0 1-4-4V7a4 4 0 0 1 4-4zm0 2a2 2 0 0 0-2 2v10a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2H7zm10.25 1.5a1.25 1.25 0 1 1 0 2.5 1.25 1.25 0 0 1 0-2.5zM12 8a4 4 0 1 1 0 8 4 4 0 0 1 0-8zm0 2a2 2 0 1 0 0 4 2 2 0 0 0 0-4z"/>
          </svg>
        </a>
      </div>
    </div>
  )
}

export default Home
